import { gql } from '@parkchirp/api-client';

export const GET_LEASING_RIGHT = gql`
    query getLeasingRight(
        $leasingRightId: Int!
    ) {
        getLeasingRight(
            leasingRightId: $leasingRightId
        ) {
            id
            rightName
            suites
            squareFeet
            commencementDate
            expirationDate
            extensionOptions
            subscriptionId
            accountId
            createdAt
            updatedAt
            createdBy
            updatedBy
            buildings {
                id
                name
            }
            facilityId
            facilityTimezone
            countAndRates {
                id
                rateId
                rateName
                grossRatePrice
                sublotId
                sublotName
                history {
                    id
                    startDate
                    endDate
                    minCount
                    maxCount
                    enforceMaxCount
                    rightToConvert
                }
                conversionRights {
                    id
                    rateName
                    sublotName
                    grossRatePrice
                    history {
                        id
                        startDate
                        endDate
                        conversionCount
                        daysRequiredForNoticeToConvert
                    }
                }
            }
            subtenants {
                id
                type
                subscriptionId
                accountId
                subscriptionName
            }
            subscriptionHasInvoice
        }
    }
`;

export const GET_LEASING_RIGHTS = gql`
    query getLeasingRights(
        $order: SortOrder
        $filters: [Filter]
        $limit: Int
        $offset: Int
        $subscriptionId: String
        $includeParentLeases: Boolean
    ) {
        getLeasingRights(
            order: $order
            filters: $filters
            limit: $limit
            offset: $offset
            subscriptionId: $subscriptionId
            includeParentLeases: $includeParentLeases
        ) {
            count
            leasingRights {
                id
                subscriptionId
                lesseeEntity
                rightName
                product
                childSubtenantAccountIds
                detachedSubtenantAccountIds
                suites
                squareFeet
                commencementDate
                expirationDate
                extensionOptions
                createdAt
                facilityName
                facilityId
                timezone
                facilityNumber
                accountId
                subscriptionName
                subscriptionStatus
                rateName
                rateId
                mustTakeRate
                mustTakeRateId
                multipleRateMustTakeDollarAmount
                spaceType
                leasingRightId
                sublotId
                sublotName
                buildings {
                    id
                    name
                    leasingRightId
                }
                leasingCountAndRateHistory {
                    id
                    startDate
                    endDate
                    minCount
                    maxCount
                    enforceMaxCount
                    mustTakeDollarAmount
                    rightToConvert
                }
                minCount
                maxCount
                enforceMaxCount
                rightToConvert
                hasConversionRights
            }
        }
    }
`;

export const VERIFY_LEASE_ACCOUNT_ID = gql`
    query verifyLeaseAccountId(
        $accountId: Int!,
    ) {
        verifyLeaseAccountId(
            accountId: $accountId
        ) {
            subscriptionId
            subscriptionName
            facilityId
        }
    }
`;

export const VERIFY_LEASE_SUBTENANT = gql`
    query verifyLeaseSubtenant(
        $leaseSubscriptionId: String!
        $subtenantAccountId: String!
        $type: String!
    ) {
        verifyLeaseSubtenant(
            leaseSubscriptionId: $leaseSubscriptionId
            subtenantAccountId: $subtenantAccountId
            type: $type
        ) {
            type
            subscriptionId
            accountId
            subscriptionName
        }
    }
`;

export const GET_BUILDINGS_BY_FACILITY_ID = gql`
    query getBuildingsByFacilityId(
        $facilityId: String!
    ) {
        getBuildingsByFacilityId(
            facilityId: $facilityId
        ) {
            id
            name
            squareFeet
            primary
        }
    }
`;

export const GET_LEASE_SUBTENANT = gql`
    query getLeaseSubtenant(
        $subscriptionId: String!
    ) {
        getLeaseSubtenant(
            subscriptionId: $subscriptionId
        ) {
            type
            parentSubscription {
                id
                accountId
                subscriptionName
            }
        }
    }
`;

export const GET_LEASE_UTILIZATION = gql`
    query getLeaseUtilization(
        $leasingRightId: Int!
    ) {
        getLeaseUtilization(
            leasingRightId: $leasingRightId
        ) {
            subscriptionId
            countsAndRates {
                id
                rateId
                rateName
                sublotId
                sublotName
                allowsConversions
                mustTakeCount
                parkerCount
            }
        }
    }
`;

export const GET_LEASING_RIGHTS_CSV = gql`
    query getLeasingRightsCsv(
        $order: SortOrder,
        $filters: [Filter],    
        $subscriptionId: String,
        $includeParentLeases: Boolean,
    ) {
        getLeasingRightsCsv(
            order: $order,
            filters: $filters,
            subscriptionId: $subscriptionId,
            includeParentLeases: $includeParentLeases,
        ) {
            url
        }
    }
`;

export const GET_PARKERS_BY_COUNT_AND_RATE = gql`
    query getParkersByCountAndRate(
        $leasingCountAndRateId: Int!,
        $filters: [Filter],
        $order: SortOrder,
        $limit: Int!
        $offset: Int!
    ) {
        getParkersByCountAndRate(
            leasingCountAndRateId: $leasingCountAndRateId,
            filters: $filters,
            order: $order
            limit: $limit
            offset: $offset
        ) {
            total
            parkers {
                id
                firstName
                lastName
                accountId
                subscriptionId
                subscriptionName
                rateName
                sublotName
            }
        }
    }
`;

export const VERIFY_SEQUENCING_TIERS_ENABLED = gql`
    query verifySequencingTiersEnabled(
        $subscriptionId: String!
    ) {
        verifySequencingTiersEnabled(
            subscriptionId: $subscriptionId
        ) {
            showSequencing
        }
    }
`;

export const GET_SEQUENCING_TIERS = gql`
    query getSequencingTiers(
        $subscriptionId: String!
    ) {
        getSequencingTiers(
            subscriptionId: $subscriptionId
        ) {
            maxSequenceNumber
            sequencingTiers {
                id
                subscriptionId
                rateId
                rateName
                sublotId
                sublotName
                tierNumber
                setNumber
                futureStartDate
                maxCount
                parkers {
                    id
                    firstName
                    lastName
                    accountId
                    sequenceNumber
                    sequenceNumberLocked
                }
            }
        }
    }
`;

export const GET_COUNTS_AND_RATES_FOR_SEQUENCING = gql`
    query getCountsAndRatesForSequencing(
        $subscriptionId: String!
    ) {
        getCountsAndRatesForSequencing(
            subscriptionId: $subscriptionId
        ) {
            countsAndRates {
                id
                rateId
                rateName
                sublotId
                sublotName
                leasingCountAndRateId,
                leasingConversionRightId
            }
        }
    }
`;

export const GET_PARKERS_BY_COUNT_AND_RATE_CSV = gql`
    query getParkersByCountAndRateCsv(
        $leasingCountAndRateId: Int!,
        $filters: [Filter],
        $order: SortOrder,
        $limit: Int!
        $offset: Int!
    ) {
        getParkersByCountAndRateCsv(
            leasingCountAndRateId: $leasingCountAndRateId,
            filters: $filters,
            order: $order
            limit: $limit
            offset: $offset
        ) {
            url
        }
    }
`;

export const GET_CONTRACT_DOCUMENTS = gql`
    query getContractDocuments {
        getContractDocuments {
            id
            subscriptionId
            contractDate
            subscriberType
            contractType
            landlordEntity
            subscriberEntity
            commencementDate
            expirationDate
            parkingContractSection
            documentContentType
            documentPath
            facilityName
            timezone
            facilityNumber
            accountId
            subscriptionName
            subscriptionStatus
        }
    }
`;

export const GET_LEASE_MANAGER_SETTINGS = gql`
    query getLeaseManagerSettings(
        $accountId: Int!
    ) {
        getLeaseManagerSettings(
            accountId: $accountId
        ) {
            id
            subscriptionId
            createdAt
            isEnabled
            abstract
        }
    }
`;

export const GET_RATES_AND_SUBLOTS_FOR_BASE_TIERS = gql`
    query getRatesAndSublotsForBaseTiers(
        $subscriptionId: String!
    ) {
        getRatesAndSublotsForBaseTiers(
            subscriptionId: $subscriptionId
        ) {
            rates {
                rateId
                rateName
                sublots {
                    sublotId
                    sublotName
                }
            }
        }
    }
`;

export const GET_FUTURE_PARKERS = gql`
    query getFutureParkers(
        $subscriptionId: String!
    ) {
        getFutureParkers(
            subscriptionId: $subscriptionId
        ) {
            parkers {
                id
                name
                accountId
                startDate
                sequenceNumber
                sequenceNumberLocked
                rate
                sublot
                tierId
            }
        }
    }
`;

export const GET_LEASE_MANAGER_ALLOCATION_REPORT = gql`
  query getLeaseManagerAllocationReport(
    $asOfDate: String!,
    $facilityId: String!,
  ) {
    getLeaseManagerAllocationReport(
        asOfDate: $asOfDate,
        facilityId: $facilityId,
    ) {
      url
    }
  }
`;

export const GET_RESEQUENCE_IN_PROGRESS = gql`
    query getResequenceSubscriptionInProgress(
        $subscriptionId: String!
    ) {
        getResequenceSubscriptionInProgress(
            subscriptionId: $subscriptionId
        ) 
    }
`;

export const CONTRACT_DOCUMENTS_SIGNED_URL = gql`
    query contractDocumentSignedUrl(
        $key: String!,
        $type: String!
    ) {
        contractDocumentSignedUrl(
            key: $key,
            type: $type
        ) {
            url
        }
    }
`;
